import { useCallback, useEffect, useRef, useState } from 'react';
import { api, errorText, formatMoney } from '../api';
import { Card, Empty, Notice, SelectField } from '../components/ui';
import type { Article, Location } from '../types';

/** Netto aus brutto, wie es die Kasse auch täte. */
const nettoAusBrutto = (brutto: number, satz: number) => Math.round(brutto / (1 + satz / 100));

interface Zeile {
  articleId: string;
  sku: string;
  name: string;
  menge: number;
  einzelBrutto: number;
  steuersatz: number;
}

interface BonAntwort {
  bon_nummer: string;
  brutto_cents: number;
  netto_cents: number;
  steuer_cents: number;
  gegeben_cents: number | null;
  rueckgeld_cents: number | null;
  signiert: boolean;
  tse_status: 'signiert' | 'ausstehend' | 'ohne';
  terminal_karte: string | null;
  terminal_beleg: string | null;
  zeilen: { sku: string; name: string; menge: number; einzel_brutto_cents: number; zeilen_brutto_cents: number }[];
}

const PLATZ_KEY = 'wawi.kasse.platz';
const MA_KEY = 'wawi.kasse.mitarbeiter';

interface Mitarbeiter {
  id: string;
  name: string;
  kuerzel: string | null;
  active: boolean;
}

interface Park {
  id: string;
  bezeichnung: string;
  mitarbeiter: string | null;
  positionen: number;
  summe_cents: number;
}

export function RegisterPage() {
  const [plaetze, setPlaetze] = useState<Location[]>([]);
  const [platz, setPlatz] = useState('');
  const [suche, setSuche] = useState('');
  const [treffer, setTreffer] = useState<Article[]>([]);
  const [zeilen, setZeilen] = useState<Zeile[]>([]);
  const [zahlart, setZahlart] = useState<'bar' | 'karte'>('bar');
  const [gegeben, setGegeben] = useState('');
  const [bon, setBon] = useState<BonAntwort | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [team, setTeam] = useState<Mitarbeiter[]>([]);
  const [mitarbeiter, setMitarbeiter] = useState('');
  const [park, setPark] = useState<Park[]>([]);
  const [maPopup, setMaPopup] = useState(false);
  const sucheRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api<Location[]>('/api/warehouse/locations')
      .then((alle) => {
        const real = alle.filter((l) => !l.is_virtual && l.active);
        setPlaetze(real);
        const gemerkt = localStorage.getItem(PLATZ_KEY);
        setPlatz(gemerkt && real.some((l) => l.id === gemerkt) ? gemerkt : real[0]?.id ?? '');
      })
      .catch((err) => setError(errorText(err)));
  }, []);

  useEffect(() => {
    if (platz) localStorage.setItem(PLATZ_KEY, platz);
  }, [platz]);

  const ladeTeam = useCallback(() => {
    api<Mitarbeiter[]>('/api/pos/mitarbeiter', { query: { aktiv: 'true' } })
      .then((alle) => {
        setTeam(alle);
        const gemerkt = localStorage.getItem(MA_KEY);
        setMitarbeiter((m) => m || (gemerkt && alle.some((x) => x.id === gemerkt) ? gemerkt : ''));
      })
      .catch(() => setTeam([]));
  }, []);
  const ladePark = useCallback(() => {
    api<Park[]>('/api/pos/park').then(setPark).catch(() => setPark([]));
  }, []);
  useEffect(() => {
    ladeTeam();
    ladePark();
  }, [ladeTeam, ladePark]);

  useEffect(() => {
    if (mitarbeiter) localStorage.setItem(MA_KEY, mitarbeiter);
  }, [mitarbeiter]);

  // Ist niemand gewählt, aber Personal vorhanden, fragt die Kasse gleich, wer
  // kassiert - über das Popup, nicht über ein verstecktes Dropdown.
  useEffect(() => {
    if (!mitarbeiter && team.length > 0) setMaPopup(true);
  }, [mitarbeiter, team.length]);

  const suchen = useCallback(async (q: string) => {
    if (!q.trim()) {
      setTreffer([]);
      return;
    }
    try {
      setTreffer(await api<Article[]>('/api/articles/lookup', { query: { q } }));
    } catch {
      setTreffer([]);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => void suchen(suche), 200);
    return () => clearTimeout(t);
  }, [suche, suchen]);

  function hinzufuegen(a: Article) {
    if (a.kind !== 'artikel') {
      setError(`${a.name} ist kein bestandsführender Artikel.`);
      return;
    }
    const preis = a.vk_brutto_cents ?? a.sale_price_cents ?? 0;
    const satz = a.tax_rate_percent ?? 19;
    setZeilen((alt) => {
      const da = alt.find((z) => z.articleId === a.id);
      if (da) return alt.map((z) => (z.articleId === a.id ? { ...z, menge: z.menge + 1 } : z));
      return [
        ...alt,
        { articleId: a.id, sku: a.sku, name: a.name, menge: 1, einzelBrutto: preis, steuersatz: satz },
      ];
    });
    setSuche('');
    setTreffer([]);
    sucheRef.current?.focus();
  }

  function menge(articleId: string, m: number) {
    if (m <= 0) {
      setZeilen((alt) => alt.filter((z) => z.articleId !== articleId));
      return;
    }
    setZeilen((alt) => alt.map((z) => (z.articleId === articleId ? { ...z, menge: m } : z)));
  }

  const brutto = zeilen.reduce((s, z) => s + Math.round(z.einzelBrutto * z.menge), 0);
  const netto = zeilen.reduce((s, z) => s + nettoAusBrutto(Math.round(z.einzelBrutto * z.menge), z.steuersatz), 0);
  const gegebenCents = gegeben.trim() ? Math.round(Number(gegeben.replace(',', '.')) * 100) : null;
  const rueckgeld = zahlart === 'bar' && gegebenCents != null ? gegebenCents - brutto : null;

  async function kassieren() {
    if (zeilen.length === 0 || !platz) return;
    setBusy(true);
    setError('');
    try {
      const antwort = await api<BonAntwort>('/api/pos/verkauf', {
        method: 'POST',
        body: {
          // Der Idempotenzschlüssel: derselbe Verkauf bucht auch bei einem
          // Doppelklick oder Netz-Wackler nur einmal.
          externalRef: crypto.randomUUID(),
          locationId: platz,
          mitarbeiterId: mitarbeiter || null,
          zahlart,
          gegebenCents: zahlart === 'bar' ? gegebenCents : null,
          zeilen: zeilen.map((z) => ({
            articleId: z.articleId,
            menge: z.menge,
            einzelBruttoCents: z.einzelBrutto,
            steuersatz: z.steuersatz,
          })),
        },
      });
      setBon(antwort);
      setZeilen([]);
      setGegeben('');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function parken() {
    if (zeilen.length === 0) return;
    setBusy(true);
    setError('');
    try {
      const wer = team.find((m) => m.id === mitarbeiter)?.name;
      await api('/api/pos/park', {
        method: 'POST',
        body: {
          bezeichnung: wer ? `${wer} · ${zeilen.length} Pos.` : `${zeilen.length} Positionen`,
          mitarbeiterId: mitarbeiter || null,
          inhalt: { zeilen },
        },
      });
      setZeilen([]);
      setGegeben('');
      ladePark();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function aufnehmen(p: Park) {
    setBusy(true);
    setError('');
    try {
      const inhalt = await api<{ zeilen: Zeile[] }>(`/api/pos/park/${p.id}`);
      setZeilen(inhalt.zeilen ?? []);
      await api(`/api/pos/park/${p.id}`, { method: 'DELETE' });
      ladePark();
      sucheRef.current?.focus();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  function neuerVerkauf() {
    setBon(null);
    setError('');
    ladePark();
    sucheRef.current?.focus();
  }

  // Nach dem Kassieren: der Bon als Beleg.
  if (bon) {
    return (
      <Card title={`Bon ${bon.bon_nummer}`}>
        {bon.tse_status === 'ausstehend' && (
          <Notice kind="error">
            Die TSE war beim Kassieren <strong>nicht erreichbar</strong>. Der Verkauf ist gebucht,
            die Signatur wird automatisch nachgeholt — der Beleg kann ausgehändigt werden.
          </Notice>
        )}
        {bon.tse_status === 'ohne' && (
          <Notice kind="info">
            Dieser Bon ist <strong>nicht signiert</strong> — es ist keine TSE angebunden
            (Entwicklungsmodus). Im Echtbetrieb trägt jeder Bon eine Signatur.
          </Notice>
        )}
        <table style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>Artikel</th>
              <th className="num">Menge</th>
              <th className="num">Einzel</th>
              <th className="num">Summe</th>
            </tr>
          </thead>
          <tbody>
            {bon.zeilen.map((z, i) => (
              <tr key={i}>
                <td>
                  <span className="mono">{z.sku}</span> {z.name}
                </td>
                <td className="num">{z.menge}</td>
                <td className="num">{formatMoney(z.einzel_brutto_cents)}</td>
                <td className="num">{formatMoney(z.zeilen_brutto_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 14, maxWidth: 320, marginLeft: 'auto' }}>
          <Summe label="Netto" cents={bon.netto_cents} />
          <Summe label="Steuer" cents={bon.steuer_cents} />
          <Summe label="Summe" cents={bon.brutto_cents} stark />
          {bon.gegeben_cents != null && <Summe label="Gegeben" cents={bon.gegeben_cents} />}
          {bon.rueckgeld_cents != null && <Summe label="Rückgeld" cents={bon.rueckgeld_cents} stark />}
        </div>
        {bon.terminal_beleg && (
          <div className="small muted" style={{ marginTop: 8 }}>
            Kartenzahlung genehmigt — {bon.terminal_karte}, Beleg {bon.terminal_beleg}
          </div>
        )}
        <button className="primary" style={{ marginTop: 16 }} onClick={neuerVerkauf} autoFocus>
          Neuer Verkauf
        </button>
      </Card>
    );
  }

  return (
    <>
      {maPopup && (
        <div className="modal-overlay" onClick={() => mitarbeiter && setMaPopup(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0 }}>Wer kassiert?</h2>
            {team.length === 0 ? (
              <p className="muted small">
                Noch kein Kassenpersonal angelegt. Unter „Kassenabschluss → Kassenpersonal"
                lassen sich Mitarbeiter hinzufügen.
              </p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {team.map((m) => (
                  <button
                    key={m.id}
                    className={m.id === mitarbeiter ? 'primary' : ''}
                    style={{ minWidth: 130, padding: '14px 16px', fontSize: '1.05em' }}
                    onClick={() => {
                      setMitarbeiter(m.id);
                      setMaPopup(false);
                    }}
                  >
                    {m.name}
                    {m.kuerzel ? ` (${m.kuerzel})` : ''}
                  </button>
                ))}
              </div>
            )}
            {mitarbeiter && (
              <button style={{ marginTop: 16 }} onClick={() => setMaPopup(false)}>
                Schließen
              </button>
            )}
          </div>
        </div>
      )}

      {error && <Notice kind="error">{error}</Notice>}

      <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: 16 }}>
        <Card title="Verkauf">
          <div className="form-grid" style={{ marginBottom: 12 }}>
            <SelectField
              label="Verkaufsplatz"
              value={platz}
              onChange={setPlatz}
              options={plaetze}
              render={(l) => `${l.code}${l.name ? ` · ${l.name}` : ''}`}
              allowEmpty={false}
            />
            <label className="field">
              <span>Wer kassiert?</span>
              <button
                type="button"
                onClick={() => setMaPopup(true)}
                style={{ textAlign: 'left', width: '100%' }}
              >
                {team.find((m) => m.id === mitarbeiter)?.name ?? '— Mitarbeiter wählen —'}
                {'  (ändern)'}
              </button>
            </label>
          </div>

          {park.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div className="small muted" style={{ marginBottom: 4 }}>Geparkte Bons</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {park.map((p) => (
                  <button
                    key={p.id}
                    className="small ghost"
                    onClick={() => aufnehmen(p)}
                    disabled={busy}
                    title={`${p.positionen} Positionen · ${formatMoney(p.summe_cents)}`}
                  >
                    ▸ {p.bezeichnung} · {formatMoney(p.summe_cents)}
                  </button>
                ))}
              </div>
            </div>
          )}
          <label className="field">
            <span>Artikel suchen oder scannen</span>
            <input
              ref={sucheRef}
              value={suche}
              autoFocus
              onChange={(e) => setSuche(e.target.value)}
              onKeyDown={(e) => {
                // Scanner schliessen mit Enter: ist genau ein Treffer da, direkt
                // übernehmen - so wird das Scannen zum Ein-Griff-Vorgang.
                if (e.key === 'Enter' && treffer.length >= 1) {
                  e.preventDefault();
                  hinzufuegen(treffer[0]!);
                }
              }}
              placeholder="Nummer, Bezeichnung oder EAN"
            />
          </label>
          {treffer.length > 0 && (
            <div className="table-wrap" style={{ marginTop: 8, maxHeight: 220, overflowY: 'auto' }}>
              <table>
                <tbody>
                  {treffer.map((a) => (
                    <tr key={a.id} onClick={() => hinzufuegen(a)} style={{ cursor: 'pointer' }}>
                      <td>
                        <span className="mono">{a.sku}</span> {a.name}
                      </td>
                      <td className="num">{formatMoney(a.vk_brutto_cents ?? a.sale_price_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="table-wrap" style={{ marginTop: 16 }}>
            <table>
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th className="num">Menge</th>
                  <th className="num">Einzel</th>
                  <th className="num">Summe</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {zeilen.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <Empty>Noch nichts auf dem Bon.</Empty>
                    </td>
                  </tr>
                ) : (
                  zeilen.map((z) => (
                    <tr key={z.articleId}>
                      <td>
                        <span className="mono">{z.sku}</span> {z.name}
                      </td>
                      <td className="num">
                        <input
                          type="number"
                          min={0}
                          value={z.menge}
                          onChange={(e) => menge(z.articleId, Number(e.target.value))}
                          style={{ width: 64, textAlign: 'right' }}
                        />
                      </td>
                      <td className="num">{formatMoney(z.einzelBrutto)}</td>
                      <td className="num">{formatMoney(Math.round(z.einzelBrutto * z.menge))}</td>
                      <td className="num">
                        <button className="small ghost" onClick={() => menge(z.articleId, 0)}>
                          entfernen
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Kassieren">
          <div style={{ maxWidth: 340 }}>
            <Summe label="Netto" cents={netto} />
            <Summe label="Steuer" cents={brutto - netto} />
            <Summe label="Zu zahlen" cents={brutto} stark />
          </div>

          <div className="tabs" style={{ marginTop: 16 }}>
            <button className={zahlart === 'bar' ? 'active' : ''} onClick={() => setZahlart('bar')}>
              Bar
            </button>
            <button className={zahlart === 'karte' ? 'active' : ''} onClick={() => setZahlart('karte')}>
              Karte
            </button>
          </div>

          {zahlart === 'bar' && (
            <label className="field" style={{ marginTop: 12 }}>
              <span>Gegeben (€)</span>
              <input
                value={gegeben}
                onChange={(e) => setGegeben(e.target.value)}
                placeholder="z. B. 20,00"
                inputMode="decimal"
              />
            </label>
          )}
          {rueckgeld != null && rueckgeld >= 0 && (
            <div style={{ marginTop: 10, maxWidth: 340 }}>
              <Summe label="Rückgeld" cents={rueckgeld} stark />
            </div>
          )}
          {rueckgeld != null && rueckgeld < 0 && (
            <Notice kind="info">Es fehlen noch {formatMoney(-rueckgeld)}.</Notice>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button
              className="primary"
              style={{ flex: 1 }}
              disabled={busy || zeilen.length === 0 || !platz || !mitarbeiter}
              onClick={kassieren}
            >
              Kassieren — {formatMoney(brutto)}
            </button>
            <button onClick={parken} disabled={busy || zeilen.length === 0}>
              Parken
            </button>
          </div>
          {zeilen.length > 0 && !mitarbeiter && (
            <div className="small muted" style={{ marginTop: 8 }}>
              Bitte zuerst wählen, wer kassiert.
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

function Summe({ label, cents, stark }: { label: string; cents: number; stark?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '4px 0',
        fontWeight: stark ? 700 : 400,
        fontSize: stark ? '1.1em' : undefined,
      }}
    >
      <span className={stark ? undefined : 'muted'}>{label}</span>
      <span>{formatMoney(cents)}</span>
    </div>
  );
}
